import React, {useEffect, useState} from 'react'
import AppBar from '@mui/material/AppBar'
import Container from '@mui/material/Container'
import Stack from '@mui/material/Stack'
import Typography from '@mui/material/Typography'
import Toolbar from '@mui/material/Toolbar'
import useModels from '../../hooks/useModels'
import useUser from '../../hooks/useUser'
import Page from '../../components/Page'
import EvalRuns from '../../components/EvalRuns'
import Summary from '../../components/evaluations/Summary'
import Model from '../../components/models/Model'
import SampleQuestions from '../../components/SampleQuestions'
import ItemLoader, {RendererProps} from '../../components/ItemLoader'
import {ModelFilters} from '../../components/filters'
import {NavButton} from '../../components/forms/inputs'
import {ColouredDivider, ColouredTypography} from '../../components/text'
import {getEvaluation, updateEvaluation} from '../../services/evaluation'
import {getEvaluationRuns} from '../../services/evaluations'
import type {Evaluation as EvaluationType, Model as ModelType} from '../../services/types'
import Path from '../../routeLinks'
import {CircularProgress} from '@mui/material'

export const fetchEvaluation = getEvaluation

const EvaluationRuns = ({evaluation}: {evaluation: EvaluationType}) => (
  <Container>
    <Stack spacing={4} direction="column">
      <EvalRuns
        evals={[evaluation]}
        itemType="model"
        searchOptions={{
          searchLabel: 'filter models',
          sortLabel: 'Displayed models:',
          sortOptions: {
            scoreDesc: 'With top average score',
            scoreAsc: 'With bottom average score',
          },
          filtersElem: ModelFilters,
        }}
      />
    </Stack>
  </Container>
)

const SampleQuestionsSection = ({id, name, samples}: EvaluationType) => (
  <AppBar color="secondary" enableColorOnDark position="relative" elevation={0}>
    <Toolbar>
      <Container sx={{pt: 6, pb: 6}}>
        <Stack spacing={2}>
          <Stack direction="row" justifyContent="space-between" alignItems="center">
            <Typography variant="h4" sx={{color: 'primary.main', fontWeight: 700}}>
              Sample Evaluation Questions
            </Typography>
            <NavButton to={Path.evaluations.tests(id)} label="Test yourself" />
          </Stack>
          {samples && <SampleQuestions questions={samples} taker={name} />}
        </Stack>
      </Container>
    </Toolbar>
  </AppBar>
)

const BestModels = ({id, name}: EvaluationType) => {
  const [best, setBest] = useState<{[k: string]: ModelType}>()
  const {models} = useModels()

  useEffect(() => {
    // FIXME: do a better way of getting the scores for this evaluation
    const getEvalSessions = async () => {
      const res = await getEvaluationRuns({filters: {evals: [{id, name}]}}, 'model')
      const scores = Object.values(res.evals)
        .map((vals) => Object.values(vals).sort((a, b) => (b.bin || 0) - (a.bin || 0))[0])
        .filter(Boolean)
        .reduce((acc, {evaluatee_id, score}) => ({...acc, [evaluatee_id]: score}), {}) as {
        [k: string]: number
      }

      const decent = models
        .map((m) => ({...m, score: scores[m.id.toString()]}))
        .filter(({score, id}) => score && score > 70)
      setBest({
        'Highest Score': decent.sort((a, b) => (b.score || 0) - (a.score || 0))[0],
        'Most Affordable': decent.sort((a, b) => (a.tokens_cost || 0) - (b.tokens_cost || 0))[0],
        'Fastest Response Time': decent.sort(
          (a, b) => (a.statistics?.median_latency || 0) - (b.statistics?.median_latency || 0)
        )[0],
      })
    }
    if (models.length > 0 && !best) {
      getEvalSessions()
    }
  }, [models, best, id, name])

  const selectedModels = best && Object.entries(best).filter(([_, model]) => model)
  return (
    <Container>
      <Stack direction="column" justifyContent="flex-start" alignItems="center" spacing={3}>
        <ColouredTypography sx={{fontWeight: 700}} variant="h4">
          Select Models for this Evaluation
        </ColouredTypography>
        <ColouredDivider sx={{width: '100%'}} />
        <Stack spacing={8} direction={{sm: 'column', md: 'row'}} sx={{pb: 5}}>
          {(selectedModels || []).length === 0 ? <CircularProgress /> : undefined}
          {selectedModels?.map(([title, model]) => (
            <Stack key={title} direction="column" alignItems="center" spacing={2}>
              <ColouredTypography variant="h5">{title}</ColouredTypography>
              <Model model={model} sx={{height: '100%', width: 300, borderRadius: 3}} />
            </Stack>
          ))}
        </Stack>
        <NavButton label="Search more models" to={Path.models.all()} />
      </Stack>
    </Container>
  )
}

const EvaluationSummary = ({item: evaluation, setItem}: RendererProps<EvaluationType>) => {
  const {canEdit} = useUser()

  const saveEvaluation = async (e: EvaluationType) => {
    const res = await updateEvaluation(e)
    if (res !== 'Evaluation updated') {
      return res
    }
    setItem({...evaluation, ...e})
    return null
  }

  return (
    <Page title={evaluation?.name} fullWidth showTitle>
      <Stack spacing={20} sx={{mt: 5, mb: 5}} alignItems="center">
        <Summary
          evaluation={evaluation}
          updateEvaluation={canEdit(evaluation) ? saveEvaluation : undefined}
        />
        <BestModels {...evaluation} />
        <EvaluationRuns evaluation={evaluation} />
        <SampleQuestionsSection {...evaluation} />
      </Stack>
    </Page>
  )
}

const Evaluation: React.FC = () => <ItemLoader itemName="evaluation" renderer={EvaluationSummary} />

export default Evaluation
